// Temporary cutover page: two independent CSV upload tools.
//   1. Override status + category on Zendesk-imported tickets.
//   2. Bulk-add time entries to Zendesk-imported tickets.
// Both lookups go through zendesk_ticket_map. Empty cells leave fields
// unchanged. Unknown values fail just that row.

import { useMemo, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface RowResult {
  zendeskTicketId: number;
  ok: boolean;
  reason?: string;
  newStatus?: string;
  newCategory?: string;
}

interface ApplyResult {
  totalRows: number;
  updated: number;
  results: RowResult[];
}

interface TimeRowResult {
  rowIndex: number;
  zendeskTicketId: number | null;
  ok: boolean;
  reason?: string;
  hours?: number;
}

interface TimeResult {
  totalRows: number;
  createdEntries: number;
  consumptionsRecorded: number;
  periodsAutoCreated: number;
  results: TimeRowResult[];
}

interface TimeByClientRowResult {
  rowIndex: number;
  client: string | null;
  ok: boolean;
  reason?: string;
  hours?: number;
}

interface TimeByClientResult {
  totalRows: number;
  createdEntries: number;
  consumptionsRecorded: number;
  periodsAutoCreated: number;
  results: TimeByClientRowResult[];
}

export function AdminTicketCsvOverridePage(): React.ReactElement {
  useDocumentTitle('Ticket CSV override');
  const { agent } = useAuth();

  // Override (status / category) upload state.
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);

  // Time upload state — separate so the two sections are independent.
  const [timeFile, setTimeFile] = useState<File | null>(null);
  const [timeBusy, setTimeBusy] = useState(false);
  const [timeError, setTimeError] = useState<string | null>(null);
  const [timeResult, setTimeResult] = useState<TimeResult | null>(null);

  // Wipe-all state.
  const [wipeBusy, setWipeBusy] = useState(false);
  const [wipeError, setWipeError] = useState<string | null>(null);
  const [wipeDeleted, setWipeDeleted] = useState<number | null>(null);

  // Time-by-client (no-ticket) upload state.
  const [byClientFile, setByClientFile] = useState<File | null>(null);
  const [byClientBusy, setByClientBusy] = useState(false);
  const [byClientError, setByClientError] = useState<string | null>(null);
  const [byClientResult, setByClientResult] = useState<TimeByClientResult | null>(null);

  const failures = useMemo(
    () => (result ? result.results.filter((r) => !r.ok) : []),
    [result],
  );
  const timeFailures = useMemo(
    () => (timeResult ? timeResult.results.filter((r) => !r.ok) : []),
    [timeResult],
  );
  const byClientFailures = useMemo(
    () => (byClientResult ? byClientResult.results.filter((r) => !r.ok) : []),
    [byClientResult],
  );

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function onApply(): Promise<void> {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiFetch<ApplyResult>('/api/admin/ticket-csv-override/apply', {
        method: 'POST',
        body: fd,
      });
      setResult(r);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
    }
  }

  async function onWipeTimeEntries(): Promise<void> {
    if (!window.confirm('Delete ALL time entries (and their block-hour consumptions)? This cannot be undone.')) {
      return;
    }
    setWipeBusy(true);
    setWipeError(null);
    setWipeDeleted(null);
    try {
      const r = await apiFetch<{ deleted: number }>(
        '/api/admin/ticket-csv-override/wipe-time-entries',
        { method: 'POST' },
      );
      setWipeDeleted(r.deleted);
      setTimeResult(null);
    } catch (e) {
      setWipeError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setWipeBusy(false);
    }
  }

  async function onApplyByClient(): Promise<void> {
    if (!byClientFile) return;
    setByClientBusy(true);
    setByClientError(null);
    setByClientResult(null);
    try {
      const fd = new FormData();
      fd.append('file', byClientFile);
      const r = await apiFetch<TimeByClientResult>(
        '/api/admin/ticket-csv-override/time-by-client',
        { method: 'POST', body: fd },
      );
      setByClientResult(r);
    } catch (e) {
      setByClientError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setByClientBusy(false);
    }
  }

  async function onApplyTime(): Promise<void> {
    if (!timeFile) return;
    setTimeBusy(true);
    setTimeError(null);
    setTimeResult(null);
    try {
      const fd = new FormData();
      fd.append('file', timeFile);
      const r = await apiFetch<TimeResult>('/api/admin/ticket-csv-override/time', {
        method: 'POST',
        body: fd,
      });
      setTimeResult(r);
    } catch (e) {
      setTimeError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setTimeBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">
          Ticket CSV override
        </h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Temporary cutover tool. Two independent CSV uploads — pick whichever
          you need. Both look up tickets by their Zendesk ID via the import
          map.
        </p>
      </div>

      <Card className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            1. Override status + category
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Columns:{' '}
            <code className="rounded bg-[var(--color-surface)] px-1 py-0.5">
              zendesk_ticket_id, category, status
            </code>
            . Empty cells leave that field unchanged.
          </p>
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setResult(null);
            setError(null);
          }}
          className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:brightness-95"
        />
        <div>
          <Button onClick={onApply} disabled={!file || busy}>
            {busy ? 'Applying…' : 'Apply CSV'}
          </Button>
        </div>
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </Card>
      {result && (
        <Card className="space-y-3">
          <p className="text-base">
            <strong>{result.updated}</strong> of{' '}
            <strong>{result.totalRows}</strong> rows updated.
            {failures.length > 0 && (
              <span className="ml-2 text-[var(--color-muted)]">
                ({failures.length} failed)
              </span>
            )}
          </p>
          {failures.length > 0 && (
            <details className="space-y-2">
              <summary className="cursor-pointer text-sm font-medium">
                Show failures
              </summary>
              <ul className="mt-2 space-y-1 text-sm">
                {failures.map((f, i) => (
                  <li key={i} className="text-[var(--color-body)]">
                    Zendesk #{f.zendeskTicketId || '?'}: {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      <Card className="space-y-3 border-red-300 bg-red-50/40">
        <div>
          <h2 className="text-base font-semibold text-red-900">
            Wipe all time entries
          </h2>
          <p className="mt-1 text-sm text-red-900">
            Retry tool. Deletes every <code>time_entries</code> row (cascading
            to <code>block_hour_consumptions</code>,{' '}
            <code>harvest_csv_import_map</code>, and{' '}
            <code>harvest_time_entry_map</code>) so a fresh time CSV import
            starts clean. Tickets, projects, and the Zendesk import map are
            not touched.
          </p>
        </div>
        <div>
          <Button variant="danger" onClick={onWipeTimeEntries} disabled={wipeBusy}>
            {wipeBusy ? 'Wiping…' : 'Wipe all time entries'}
          </Button>
        </div>
        {wipeError && (
          <p className="rounded-md bg-red-100 px-3 py-2 text-sm text-red-800">
            {wipeError}
          </p>
        )}
        {wipeDeleted !== null && (
          <p className="rounded-md bg-green-100 px-3 py-2 text-sm text-green-800">
            Deleted <strong>{wipeDeleted}</strong> time entries.
          </p>
        )}
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            2. Bulk-add time entries
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Required columns:{' '}
            <code className="rounded bg-[var(--color-surface)] px-1 py-0.5">
              zendesk_ticket_number, type, hours
            </code>
            . Optional:{' '}
            <code className="rounded bg-[var(--color-surface)] px-1 py-0.5">
              date
            </code>{' '}
            (M/D/YYYY or YYYY-MM-DD; defaults to today). One{' '}
            <code>time_entries</code> row per CSV row. <strong>Type</strong>{' '}
            accepts: <em>Block Hours</em>, <em>Project</em>,{' '}
            <em>Internal Projects</em>. Agent on each entry is you (no
            technician column). is_billed=true. <strong>No idempotency</strong>{' '}
            — re-uploading creates duplicates.
          </p>
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setTimeFile(e.target.files?.[0] ?? null);
            setTimeResult(null);
            setTimeError(null);
          }}
          className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:brightness-95"
        />
        <div>
          <Button onClick={onApplyTime} disabled={!timeFile || timeBusy}>
            {timeBusy ? 'Importing…' : 'Import time entries'}
          </Button>
        </div>
        {timeError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {timeError}
          </p>
        )}
      </Card>
      {timeResult && (
        <Card className="space-y-3">
          <p className="text-base">
            <strong>{timeResult.createdEntries}</strong> of{' '}
            <strong>{timeResult.totalRows}</strong> time entries created.
            {timeResult.consumptionsRecorded > 0 && (
              <>
                {' '}
                <strong>{timeResult.consumptionsRecorded}</strong> recorded as
                block-hour consumptions.
              </>
            )}
            {timeResult.periodsAutoCreated > 0 && (
              <>
                {' '}
                Auto-created{' '}
                <strong>{timeResult.periodsAutoCreated}</strong> historical
                block-hour period{timeResult.periodsAutoCreated === 1 ? '' : 's'}.
              </>
            )}
            {timeFailures.length > 0 && (
              <span className="ml-2 text-[var(--color-muted)]">
                ({timeFailures.length} failed)
              </span>
            )}
          </p>
          {timeFailures.length > 0 && (
            <details className="space-y-2">
              <summary className="cursor-pointer text-sm font-medium">
                Show failures
              </summary>
              <ul className="mt-2 space-y-1 text-sm">
                {timeFailures.map((f, i) => (
                  <li key={i} className="text-[var(--color-body)]">
                    Row {f.rowIndex}
                    {f.zendeskTicketId ? ` (Zendesk #${f.zendeskTicketId})` : ''}:{' '}
                    {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      <Card className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            3. Bulk-add time entries by client (no ticket)
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            For Harvest exports that are calendar / meeting time — bucketed by
            client but never tied to a Zendesk ticket. Required columns:{' '}
            <code className="rounded bg-[var(--color-surface)] px-1 py-0.5">
              client, project, hours
            </code>
            . Optional:{' '}
            <code className="rounded bg-[var(--color-surface)] px-1 py-0.5">
              date, notes, billable
            </code>
            . Each row becomes a ticketless{' '}
            <code>time_entries</code> row attributed directly to the matched
            org. <em>Project</em> column must start with{' '}
            <em>Block Hours</em>, <em>Project</em>, or{' '}
            <em>Internal Projects</em> (variants like{' '}
            <em>"Block Hours - 10 Hours Monthly"</em> match). Rows where{' '}
            <em>Billable? = No</em> are skipped. "T3 Internal" client name
            maps to the T3 Tech Partners org. No idempotency — re-uploading
            creates duplicates; use the wipe button above first.
          </p>
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setByClientFile(e.target.files?.[0] ?? null);
            setByClientResult(null);
            setByClientError(null);
          }}
          className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:brightness-95"
        />
        <div>
          <Button onClick={onApplyByClient} disabled={!byClientFile || byClientBusy}>
            {byClientBusy ? 'Importing…' : 'Import time entries by client'}
          </Button>
        </div>
        {byClientError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {byClientError}
          </p>
        )}
      </Card>
      {byClientResult && (
        <Card className="space-y-3">
          <p className="text-base">
            <strong>{byClientResult.createdEntries}</strong> of{' '}
            <strong>{byClientResult.totalRows}</strong> time entries created.
            {byClientResult.consumptionsRecorded > 0 && (
              <>
                {' '}
                <strong>{byClientResult.consumptionsRecorded}</strong> recorded
                as block-hour consumptions.
              </>
            )}
            {byClientResult.periodsAutoCreated > 0 && (
              <>
                {' '}
                Auto-created{' '}
                <strong>{byClientResult.periodsAutoCreated}</strong> historical
                block-hour period
                {byClientResult.periodsAutoCreated === 1 ? '' : 's'}.
              </>
            )}
            {byClientFailures.length > 0 && (
              <span className="ml-2 text-[var(--color-muted)]">
                ({byClientFailures.length} failed / skipped)
              </span>
            )}
          </p>
          {byClientFailures.length > 0 && (
            <details className="space-y-2">
              <summary className="cursor-pointer text-sm font-medium">
                Show failures
              </summary>
              <ul className="mt-2 space-y-1 text-sm">
                {byClientFailures.map((f, i) => (
                  <li key={i} className="text-[var(--color-body)]">
                    Row {f.rowIndex}
                    {f.client ? ` (${f.client})` : ''}: {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}
    </div>
  );
}
